import { useEffect, useMemo, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  getImageUrl,
  locationApi,
  serviceApi,
  userMasterApi,
  userRoleApi,
} from "../../api/masterApi";
import DropdownMultiSelect from "../../components/DropdownMultiSelect";
import { buildAppPath } from "../../utils/auth";
import "./UserMaster.css";

const emptyForm = () => ({
  user_role_id: "",
  is_establishment: false,
  user_title: "",
  first_name: "",
  last_name: "",
  gender: "",
  age: "",
  experience_years: "",
  linked_asset_ids: [],
  profile_picture: "",
  location_id: "",
  country_code: "",
  country_name: "",
  state_name: "",
  phone_number: "",
  status: "Active",
});

/** Walk location → parent chain to resolve country dial code / names. */
function resolveLocationCountry(locations, locationId) {
  const byId = new Map(
    (locations || []).map((location) => [String(location.location_id), location])
  );
  let current = byId.get(String(locationId));
  let countryCode = "";
  let countryName = "";
  let stateName = "";
  let guard = 0;

  while (current && guard < 20) {
    const typeName = String(current.location_type_name || "").toLowerCase();
    if (!countryCode && current.country_code) {
      countryCode = String(current.country_code);
    }
    if (typeName.includes("state") && !stateName) {
      stateName = current.location_name || "";
    }
    if (typeName.includes("country")) {
      countryName = current.location_name || countryName;
      if (current.country_code) countryCode = String(current.country_code);
      break;
    }
    if (!current.parent_location_id) {
      if (!countryName) countryName = current.location_name || "";
      break;
    }
    current = byId.get(String(current.parent_location_id));
    guard += 1;
  }

  return { country_code: countryCode, country_name: countryName, state_name: stateName };
}

function Section({ number, title, children }) {
  return (
    <section className="user-master-section">
      <h3>{number}. {title}</h3>
      <div className="user-master-grid">{children}</div>
    </section>
  );
}

export default function AdminUserMaster() {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [profilePicture, setProfilePicture] = useState(null);
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loginUserIds, setLoginUserIds] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const showToast = (message, severity = "success") =>
    setToast({ open: true, message, severity });

  const loadData = async () => {
    setLoading(true);
    try {
      const [users, roleList, locationList, assetList] = await Promise.all([
        userMasterApi.list(),
        userRoleApi.list(),
        locationApi.list(),
        serviceApi.list(),
      ]);
      setRows(users || []);
      setRoles(roleList || []);
      setLocations(locationList || []);
      setAssets(assetList || []);
      setLoginUserIds(
        new Set(
          (users || [])
            .filter((user) => user.login_id)
            .map((user) => String(user.user_id))
        )
      );
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locationOptions = useMemo(
    () =>
      [...locations].sort((a, b) =>
        String(a.location_name || "").localeCompare(String(b.location_name || ""))
      ),
    [locations]
  );

  const assetOptions = useMemo(
    () =>
      (assets || []).map((asset) => ({
        value: String(asset.service_id),
        label: asset.service_title || "Asset",
      })),
    [assets]
  );

  const assetLabelMap = useMemo(() => {
    const map = new Map();
    assetOptions.forEach((option) => map.set(String(option.value), option.label));
    return map;
  }, [assetOptions]);

  const setValue = (name, value) =>
    setForm((previous) => ({ ...previous, [name]: value }));

  const handleLocationChange = (locationId) => {
    if (!locationId) {
      setForm((previous) => ({
        ...previous,
        location_id: "",
        country_code: "",
        country_name: "",
        state_name: "",
      }));
      return;
    }
    const resolved = resolveLocationCountry(locations, locationId);
    setForm((previous) => ({
      ...previous,
      location_id: locationId,
      country_code: resolved.country_code,
      country_name: resolved.country_name,
      state_name: resolved.state_name,
    }));
  };

  const renderField = ({
    name,
    label,
    type = "text",
    options = [],
    required = false,
    placeholder = "",
  }) => (
    <div className="user-master-field" key={name}>
      <label htmlFor={name}>
        {label}
        {required && <span className="user-master-req"> *</span>}
      </label>
      {type === "select" ? (
        <select
          id={name}
          value={form[name]}
          required={required}
          onChange={(event) => setValue(name, event.target.value)}
        >
          <option value="">Select {label.toLowerCase()}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : type === "boolean" ? (
        <select
          id={name}
          value={form[name] ? "1" : "0"}
          onChange={(event) => setValue(name, event.target.value === "1")}
        >
          <option value="0">No</option>
          <option value="1">Yes</option>
        </select>
      ) : (
        <input
          id={name}
          type={type}
          value={form[name]}
          required={required}
          placeholder={placeholder}
          onChange={(event) => setValue(name, event.target.value)}
        />
      )}
    </div>
  );

  const reset = () => {
    setForm(emptyForm());
    setProfilePicture(null);
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.user_role_id || !form.first_name.trim() || !form.phone_number.trim()) {
      showToast("User Role, First Name and Phone Number are required", "error");
      return;
    }

    setSaving(true);
    try {
      const payload = new FormData();
      [
        "user_role_id",
        "user_title",
        "first_name",
        "last_name",
        "gender",
        "age",
        "experience_years",
        "location_id",
        "country_code",
        "status",
      ].forEach((name) => payload.append(name, form[name] ?? ""));
      payload.append("is_establishment", form.is_establishment ? "1" : "0");
      payload.append("phone_numbers", JSON.stringify([form.phone_number.trim()]));
      payload.append(
        "linked_asset_ids",
        JSON.stringify((form.linked_asset_ids || []).map(Number).filter(Boolean))
      );

      if (profilePicture) {
        payload.append("profile_picture", profilePicture);
      } else if (form.profile_picture) {
        payload.append(
          "profile_picture",
          String(form.profile_picture).split("/").pop()
        );
      }

      if (editingId) {
        await userMasterApi.update(editingId, payload);
      } else {
        await userMasterApi.create(payload);
      }
      showToast(editingId ? "User updated successfully" : "User created successfully");
      reset();
      await loadData();
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    const resolved =
      item.location_id && !item.country_code
        ? resolveLocationCountry(locations, item.location_id)
        : null;
    setForm({
      user_role_id: String(item.user_role_id || ""),
      is_establishment: Number(item.is_establishment) === 1,
      user_title: item.user_title || "",
      first_name: item.first_name || "",
      last_name: item.last_name || "",
      gender: item.gender || "",
      age: item.age ?? "",
      experience_years: item.experience_years ?? "",
      linked_asset_ids: Array.isArray(item.linked_asset_ids)
        ? item.linked_asset_ids.map(String)
        : [],
      profile_picture: item.profile_picture || "",
      location_id: String(item.location_id || ""),
      country_code: item.country_code || resolved?.country_code || "",
      country_name: item.country_name || resolved?.country_name || "",
      state_name: item.state_name || resolved?.state_name || "",
      phone_number: item.phone_numbers?.[0] || "",
      status: item.status || "Active",
    });
    setProfilePicture(null);
    setEditingId(item.user_id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await userMasterApi.remove(id);
      showToast("User deleted successfully");
      await loadData();
    } catch (error) {
      showToast(error.message, "error");
    }
  };

  const handleCreateLogin = (item) => {
    const params = new URLSearchParams({
      user_id: String(item.user_id),
      user_role_id: String(item.user_role_id),
    });
    navigate(`${buildAppPath("create-login")}?${params.toString()}`);
  };

  const roleOptions = roles.map((role) => ({
    value: role.user_role_id,
    label: role.user_role,
  }));

  return (
    <div className="user-master-page">
      <div className="user-master-header">
        <h2>User Master</h2>
        <p>Add and manage user basic information including location and phone.</p>
      </div>

      <form onSubmit={handleSubmit}>
        <Section number="1" title="Basic Information">
          {renderField({
            name: "user_role_id",
            label: "User Role",
            type: "select",
            options: roleOptions,
            required: true,
          })}
          {renderField({
            name: "is_establishment",
            label: "Is Establishment",
            type: "boolean",
          })}
          {renderField({
            name: "user_title",
            label: "User Title",
            type: "select",
            options: ["Dr", "Mr", "Ms"].map((value) => ({ value, label: value })),
          })}
          {renderField({ name: "first_name", label: "First Name", required: true })}
          {renderField({ name: "last_name", label: "Last Name" })}
          {renderField({
            name: "gender",
            label: "Gender",
            type: "select",
            options: ["Male", "Female", "Other"].map((value) => ({
              value,
              label: value,
            })),
          })}
          {renderField({ name: "age", label: "Age", type: "number" })}
          {renderField({
            name: "experience_years",
            label: "Experience in Years",
            type: "number",
          })}
          <div className="user-master-field user-master-field--full">
            <label htmlFor="linked_asset_ids">Linked Asset</label>
            <DropdownMultiSelect
              id="linked_asset_ids"
              options={assetOptions}
              value={form.linked_asset_ids || []}
              placeholder="Select linked assets"
              onChange={(next) => setValue("linked_asset_ids", next)}
            />
          </div>
          <div className="user-master-field">
            <label htmlFor="profile_picture">Profile Picture</label>
            <input
              id="profile_picture"
              type="file"
              accept="image/*"
              onChange={(event) =>
                setProfilePicture(event.target.files?.[0] || null)
              }
            />
            {form.profile_picture && !profilePicture && (
              <div className="user-master-existing">
                <a href={getImageUrl(form.profile_picture)} target="_blank" rel="noreferrer">
                  Current profile picture
                </a>
              </div>
            )}
          </div>
          {renderField({
            name: "status",
            label: "Status",
            type: "select",
            options: ["Active", "Suspended", "Blocked"].map((value) => ({
              value,
              label: value,
            })),
          })}
          <div className="user-master-field">
            <label htmlFor="location_id">Location / Country</label>
            <select
              id="location_id"
              value={form.location_id}
              onChange={(event) => handleLocationChange(event.target.value)}
            >
              <option value="">Select location</option>
              {locationOptions.map((location) => (
                <option key={location.location_id} value={location.location_id}>
                  {location.location_name}
                </option>
              ))}
            </select>
          </div>
          <div className="user-master-field">
            <label htmlFor="phone_number">
              Phone Number
              <span className="user-master-req"> *</span>
            </label>
            <div className="user-master-phone">
              <span className="user-master-phone__code" title="Country code from location">
                {form.country_code || "—"}
              </span>
              <input
                id="phone_number"
                type="tel"
                value={form.phone_number}
                required
                placeholder="Phone number"
                onChange={(event) => setValue("phone_number", event.target.value)}
              />
            </div>
          </div>
        </Section>

        <div className="user-master-actions">
          {editingId && (
            <button type="button" onClick={reset}>
              Cancel
            </button>
          )}
          <button type="submit" className="user-master-save" disabled={saving}>
            {saving ? "Saving..." : editingId ? "Update User" : "Save User"}
          </button>
        </div>
      </form>

      <section className="user-master-section user-master-list">
        <h3>Saved Users</h3>
        <div className="master-table-wrap">
          <table className="master-table">
            <thead>
              <tr>
                <th>#</th>
                <th>User</th>
                <th>Role</th>
                <th>Name</th>
                <th>Linked Assets</th>
                <th>Location</th>
                <th>Phone Number</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan="9" className="master-table__empty">
                    No users yet.
                  </td>
                </tr>
              )}
              {rows.map((item, index) => {
                const hasLogin = loginUserIds.has(String(item.user_id));
                const linkedLabels = (item.linked_asset_ids || [])
                  .map((id) => assetLabelMap.get(String(id)) || String(id))
                  .join(", ");
                return (
                  <tr key={item.user_id}>
                    <td>{index + 1}</td>
                    <td>{item.user_id}</td>
                    <td>{item.user_role}</td>
                    <td>
                      {[item.user_title, item.first_name, item.last_name]
                        .filter(Boolean)
                        .join(" ")}
                    </td>
                    <td>{linkedLabels || "—"}</td>
                    <td>
                      {item.location_name || "—"}
                    </td>
                    <td>
                      {item.country_code
                        ? `${item.country_code} ${item.phone_numbers?.[0] || ""}`.trim()
                        : item.phone_numbers?.[0] || "—"}
                    </td>
                    <td>{item.status}</td>
                    <td>
                      <div className="master-table__actions">
                        <button
                          type="button"
                          className="master-btn master-btn--primary"
                          disabled={hasLogin}
                          onClick={() => handleCreateLogin(item)}
                        >
                          {hasLogin ? "Login Created" : "Create Login"}
                        </button>
                        <button
                          type="button"
                          className="master-btn master-btn--edit"
                          onClick={() => handleEdit(item)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="master-btn master-btn--delete"
                          onClick={() => handleDelete(item.user_id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((previous) => ({ ...previous, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((previous) => ({ ...previous, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
